import { fetchData } from "../../../../fetchData";
import {
  AgentId,
  AgentInfo,
  AIProviderName,
  ChatMessage,
  ChatSummary,
  SendMessageResult,
} from "./types";

export const listAgents = (): Promise<AgentInfo[]> =>
  fetchData("/nemo/agents", { method: "GET" });

export const listChats = (): Promise<ChatSummary[]> =>
  fetchData("/nemo/chats", { method: "GET" });

export const createChat = (
  provider?: AIProviderName,
  agentId?: AgentId | null,
): Promise<ChatSummary> =>
  fetchData("/nemo/chats", {
    method: "POST",
    body: JSON.stringify({ provider, agentId: agentId ?? undefined }),
  });

export const getChatMessages = (chatId: string): Promise<ChatMessage[]> =>
  fetchData(`/nemo/chats/${chatId}/messages`, { method: "GET" });

export async function downloadAttachment(storageKey: string): Promise<void> {
  const result = await fetchData(
    `/cloud/share?key=${encodeURIComponent(storageKey)}`,
    { method: "GET" },
  );

  if (result?.url) {
    window.open(result.url, "_blank");
  }
}

type StreamDeltaEvent = { type: "delta"; text: string };
type StreamStatusEvent = { type: "status"; label: string };
type StreamDoneEvent = { type: "done" } & SendMessageResult;
type StreamErrorEvent = { type: "error"; message: string };
type StreamEvent =
  | StreamDeltaEvent
  | StreamStatusEvent
  | StreamDoneEvent
  | StreamErrorEvent;
type StreamFinalEvent = StreamDoneEvent | StreamErrorEvent;

type SendMessageStreamParams = {
  chatId: string;
  message: string;
  files: File[];
  onDelta: (text: string) => void;
  onStatus?: (label: string) => void;
  signal?: AbortSignal;
};

/**
 * Envia a mensagem (multipart, com anexos) e lê a resposta em streaming.
 * Não usa `fetchData` (que força Content-Type: application/json, incompatível
 * com multipart/form-data) nem axios (não expõe leitura progressiva do corpo
 * da resposta no browser). Usa `fetch` nativo + `ReadableStream`.
 */
export async function sendMessageStream({
  chatId,
  message,
  files,
  onDelta,
  onStatus,
  signal,
}: SendMessageStreamParams): Promise<StreamFinalEvent> {
  const token = localStorage.getItem(`${process.env.REACT_APP_KEYNAME}token`);
  const formData = new FormData();

  if (message) formData.append("message", message);
  files.forEach((file) => formData.append("files", file));

  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/nemo/chats/${chatId}/messages`,
    {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      body: formData,
      signal,
    },
  );

  if (!response.ok || !response.body) {
    let serverMessage = "";
    try {
      const data = await response.json();
      serverMessage =
        typeof data?.message === "string"
          ? data.message
          : Array.isArray(data?.message)
          ? data.message.join(" ")
          : "";
    } catch {
      serverMessage = "";
    }
    throw new Error(serverMessage || `HTTP error ${response.status}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let finalEvent: StreamFinalEvent = {
    type: "error",
    message: "Sem resposta do servidor.",
  };

  const consumeLine = (line: string) => {
    if (!line.trim()) return;
    const event: StreamEvent = JSON.parse(line);

    if (event.type === "delta") {
      onDelta(event.text);
    } else if (event.type === "status") {
      onStatus?.(event.label);
    } else {
      finalEvent = event;
    }
  };

  let streamDone = false;
  while (!streamDone) {
    const { done, value } = await reader.read();
    streamDone = done;
    if (value) {
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() || "";
      lines.forEach(consumeLine);
    }
  }

  if (buffer.trim()) consumeLine(buffer);

  return finalEvent;
}
